<script setup lang="ts">
import { isUserSpotlightRoute } from '~~/shared/utils/spotlight'

const route = useRoute()
const auth = useAuth()
const showPlatformTopbar = computed(
  () =>
    !['/q/respond', '/audit-room'].includes(route.path) &&
    auth.authenticated.value &&
    !auth.tenantSelectionRequired.value &&
    !['/login', '/setup', '/tenant-select'].includes(route.path),
)
const showGlobalSpotlightSearch = computed(
  () =>
    route.path !== '/audit-room' &&
    auth.authenticated.value &&
    Boolean(auth.tenant.value) &&
    isUserSpotlightRoute(route.path),
)
const changelog = useChangelog()
const tenantId = computed(() => auth.tenant.value?.id)
onMounted(() =>
  watch(
    tenantId,
    async (id) => {
      changelog.dismissed.value = true
      if (
        route.path === '/audit-room' ||
        !id ||
        !auth.authenticated.value ||
        auth.mode.value !== 'normal'
      )
        return
      try {
        await changelog.refresh()
      } catch {
        changelog.dismissed.value = true
      }
    },
    { immediate: true },
  ),
)
const showChangelog = computed(() =>
  Boolean(
    !['/q/respond', '/audit-room'].includes(route.path) &&
    auth.authenticated.value &&
    tenantId.value &&
    !changelog.dismissed.value &&
    changelog.status.value?.releases.length,
  ),
)
const socket = useNotificationSocket()
onMounted(() => {
  if (!['/q/respond', '/audit-room'].includes(route.path)) socket.start()
})
onBeforeUnmount(() => socket.stop())
</script>

<template>
  <UiPageShell v-if="auth.switching.value || auth.contextUnconfirmed.value">
    <UiEmptyState
      title="Actualisation de votre entreprise"
      :description="
        auth.switching.value || auth.pending.value
          ? 'Vérification du contexte et des autorisations en cours.'
          : 'Le contexte n’a pas pu être confirmé. Réessayez ou reconnectez-vous.'
      "
      role="status"
    >
      <template
        v-if="!auth.switching.value"
        #actions
      >
        <UiButton
          :loading="auth.pending.value"
          @click="auth.refresh()"
          >Réessayer</UiButton
        >
        <UiButton
          variant="outline"
          @click="navigateTo('/login', { external: true })"
          >Se reconnecter</UiButton
        >
      </template>
    </UiEmptyState>
  </UiPageShell>
  <template v-else>
    <DashboardGlobalSpotlightSearch v-if="showGlobalSpotlightSearch" />
    <DashboardPlatformTopbar v-if="showPlatformTopbar" />
    <NuxtPage />
    <ChangelogReleaseModal
      v-if="showChangelog && changelog.status.value"
      :releases="changelog.status.value.releases"
      :reason="changelog.status.value.reason"
      :saving="changelog.saving.value"
      :error="changelog.error.value"
      @close="changelog.dismissed.value = true"
      @acknowledge="changelog.acknowledge()"
    />
  </template>
</template>
